// Instants and company-local dates (technical design §6.1). Instants are ISO-8601 UTC strings
// with milliseconds ('2026-10-20T16:05:00.000Z'); local dates are 'YYYY-MM-DD' in the company's
// time zone, which all business-day logic (after hours, end of day) uses.

export type Instant = string;
export type LocalDate = string;
export type LocalTime = string; // 'HH:mm' or 'HH:mm:ss'

const INSTANT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,9})?(Z|[+-]\d{2}:\d{2})$/;
const LOCAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

export function isInstant(value: unknown): value is Instant {
  return typeof value === 'string' && INSTANT_RE.test(value) && !Number.isNaN(Date.parse(value));
}

/** Normalises any ISO instant (with offset) to UTC 'Z' form with milliseconds. */
export function toInstant(value: Date | string | number): Instant {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) throw new RangeError(`invalid instant: ${String(value)}`);
  return d.toISOString();
}

export function isLocalDate(value: unknown): value is LocalDate {
  if (typeof value !== 'string') return false;
  const m = LOCAL_DATE_RE.exec(value);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!));
  return d.getUTCFullYear() === +m[1]! && d.getUTCMonth() === +m[2]! - 1 && d.getUTCDate() === +m[3]!;
}

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string): Intl.DateTimeFormat {
  let f = formatters.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(tz, f);
  }
  return f;
}

export interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export function zonedParts(instant: Instant | Date, tz: string): ZonedParts {
  const d = instant instanceof Date ? instant : new Date(instant);
  const parts: Record<string, number> = {};
  for (const p of formatter(tz).formatToParts(d)) if (p.type !== 'literal') parts[p.type] = Number(p.value);
  return {
    year: parts.year!,
    month: parts.month!,
    day: parts.day!,
    hour: parts.hour!,
    minute: parts.minute!,
    second: parts.second!,
  };
}

/** Offset of the zone from UTC at that instant, in minutes (Vancouver in summer: -420). */
export function offsetMinutes(instant: Instant | Date, tz: string): number {
  const d = instant instanceof Date ? instant : new Date(instant);
  const p = zonedParts(d, tz);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return Math.round((asUtc - Math.floor(d.getTime() / 1000) * 1000) / 60000);
}

export function localDateOf(instant: Instant | Date, tz: string): LocalDate {
  const p = zonedParts(instant, tz);
  return `${String(p.year).padStart(4, '0')}-${pad(p.month)}-${pad(p.day)}`;
}

/**
 * The instant at a wall-clock time on a local date. In a spring-forward gap the time does not
 * exist; it resolves to the instant just after the gap. In a fall-back overlap the earlier of the
 * two instants is returned.
 */
export function instantAt(date: LocalDate, time: LocalTime, tz: string): Instant {
  if (!isLocalDate(date)) throw new RangeError(`invalid local date: ${date}`);
  const t = LOCAL_TIME_RE.exec(time);
  if (!t) throw new RangeError(`invalid local time: ${time}`);
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number];
  const wall = Date.UTC(y, mo - 1, d, +t[1]!, +t[2]!, t[3] ? +t[3] : 0);
  // Try the offsets in effect a day either side; one of them (or the gap edge) maps back to the wall time.
  const candidates = new Set([
    offsetMinutes(new Date(wall - 86_400_000), tz),
    offsetMinutes(new Date(wall), tz),
    offsetMinutes(new Date(wall + 86_400_000), tz),
  ]);
  const matches = [...candidates]
    .map((off) => wall - off * 60_000)
    .filter((ms) => {
      const p = zonedParts(new Date(ms), tz);
      return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) === wall;
    })
    .sort((a, b) => a - b);
  if (matches.length) return new Date(matches[0]!).toISOString();
  // Gap: the wall time is skipped. Use the later offset (after the transition).
  const after = Math.min(...[...candidates]);
  return new Date(wall - after * 60_000).toISOString();
}

export function startOfLocalDay(date: LocalDate, tz: string): Instant {
  return instantAt(date, '00:00', tz);
}

export function addDays(date: LocalDate, days: number): LocalDate {
  if (!isLocalDate(date)) throw new RangeError(`invalid local date: ${date}`);
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const r = new Date(Date.UTC(y, m - 1, d + days));
  return `${String(r.getUTCFullYear()).padStart(4, '0')}-${pad(r.getUTCMonth() + 1)}-${pad(r.getUTCDate())}`;
}

export function daysBetween(from: LocalDate, to: LocalDate): number {
  const ms = (s: LocalDate) => {
    const [y, m, d] = s.split('-').map(Number) as [number, number, number];
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((ms(to) - ms(from)) / 86_400_000);
}

// ── words: dates and times as the product writes them, on every screen and in every text ──
// One style (en-CA): month first ("Oct 15, 2026", "Thu, Oct 15"), and times as "5:18 p.m.". Screens
// and messages use these, never their own Intl options, so the office, the iPad, the portal and the
// texts all read alike.

const LOCALE = 'en-CA';
/** Intl may put a narrow or no-break space before "a.m."; text the product writes uses a plain one. */
const plain = (s: string) => s.replace(/[\u202f\u00a0]/g, ' ');
type At = Instant | Date;
const asDate = (at: At) => (at instanceof Date ? at : new Date(at));

/** "5:18 p.m." in the time zone. */
export function timeText(at: At, timeZone: string): string {
  return plain(
    new Intl.DateTimeFormat(LOCALE, { hour: 'numeric', minute: '2-digit', timeZone }).format(asDate(at)),
  );
}

/** "5:18" and "p.m." apart, for a large time with a small suffix. */
export function timeParts(at: At, timeZone: string): { time: string; suffix: string } {
  const [time = '', suffix = ''] = timeText(at, timeZone).split(' ');
  return { time, suffix };
}

const range = (a: { time: string; suffix: string }, b: { time: string; suffix: string }) =>
  a.suffix === b.suffix ? `${a.time}–${b.time} ${b.suffix}` : `${a.time} ${a.suffix}–${b.time} ${b.suffix}`;

/** "8:00–10:00 a.m.", or across noon "11:00 a.m.–1:00 p.m.": a window between two instants. */
export function timeRangeText(start: At, end: At, timeZone: string): string {
  return range(timeParts(start, timeZone), timeParts(end, timeZone));
}

/** A wall-clock time, "17:00" or "17:00:00", as "5:00 p.m." apart; no zone, it is already local. */
function localParts(t: LocalTime): { time: string; suffix: string } {
  const [hh = 0, mm = 0] = t.split(':').map(Number);
  return {
    time: `${((hh + 11) % 12) + 1}:${String(mm).padStart(2, '0')}`,
    suffix: hh % 24 < 12 ? 'a.m.' : 'p.m.',
  };
}

/** "5:00 p.m." for a wall-clock "17:00". */
export function localTimeText(t: LocalTime): string {
  const p = localParts(t);
  return `${p.time} ${p.suffix}`;
}

/**
 * "8 a.m.–5 p.m." for wall-clock "08:00" and "17:00" (office hours, quiet hours): minutes only where
 * an end has them ("9–11:30 p.m."), the house style windows have too (QA-393).
 */
export function localTimeRangeText(start: LocalTime, end: LocalTime): string {
  const bare = (p: { time: string; suffix: string }) => ({ ...p, time: p.time.replace(/:00$/, '') });
  return range(bare(localParts(start)), bare(localParts(end)));
}

/** "8–10 a.m.", "12–2 p.m.", "10 p.m.–12 a.m.": a window in whole local hours (0–24, past 24 wraps). */
export function hourRangeText(startHour: number, endHour: number): string {
  const at = (x: number) => ({
    time: String((((x % 24) + 11) % 12) + 1),
    suffix: x % 24 < 12 ? 'a.m.' : 'p.m.',
  });
  return range(at(startHour), at(endHour));
}

/**
 * A visit's window as every text and screen writes it (QA-393): minutes only where an end has them,
 * end by end. "8–10 a.m." (as the booking confirmation reads, hourRangeText), "11 a.m.–1 p.m.",
 * "8:30–10:30 a.m.", "11:30 a.m.–1 p.m.", "10 p.m.–12 a.m.".
 */
export function windowText(start: At, end: At, timeZone: string): string {
  const bare = (p: { time: string; suffix: string }) => ({ ...p, time: p.time.replace(/:00$/, '') });
  return range(bare(timeParts(start, timeZone)), bare(timeParts(end, timeZone)));
}

/**
 * "Oct 15, 2026", or with `{ year: false }` "Oct 15", with `{ weekday: 'short' }` "Thu, Oct 15, 2026".
 * A LocalDate is a calendar day and is read as one (the zone can't move it); an instant is read in the zone.
 * Given `now`, `{ year: false }` leaves the year off only a day in now's year (in the zone): an older or
 * later one keeps it, "Oct 2, 2024" (history fix 4, 22 Oct: imported records go back years). A screen
 * passes its today; with no `now` it is left off, as before.
 */
export function dateText(
  at: At | LocalDate,
  timeZone: string,
  o: { year?: boolean; weekday?: 'short' | 'long'; now?: At | LocalDate } = {},
): string {
  const day = typeof at === 'string' && isLocalDate(at);
  const yearOf = (x: At | LocalDate) =>
    typeof x === 'string' && isLocalDate(x) ? Number(x.slice(0, 4)) : zonedParts(asDate(x), timeZone).year;
  const noYear = o.year === false && (o.now === undefined || yearOf(at) === yearOf(o.now));
  return plain(
    new Intl.DateTimeFormat(LOCALE, {
      month: 'short',
      day: 'numeric',
      ...(noYear ? {} : { year: 'numeric' }),
      ...(o.weekday ? { weekday: o.weekday } : {}),
      timeZone: day ? 'UTC' : timeZone,
    }).format(day ? new Date(`${at}T12:00:00Z`) : asDate(at as At)),
  );
}

/** "Oct 15, 2026, 5:18 p.m.", or with `{ year: false }` "Oct 15, 5:18 p.m." (given `now`, in its year). */
export function dateTimeText(
  at: At,
  timeZone: string,
  o: { year?: boolean; now?: At | LocalDate } = {},
): string {
  return `${dateText(at, timeZone, o)}, ${timeText(at, timeZone)}`;
}

/**
 * Sentences joined, each ending in one period: a sentence that ends on a time ("… at 5:18 p.m.")
 * takes no second one. Empty parts are left out.
 */
export function sentences(...parts: readonly (string | false | null | undefined)[]): string {
  return parts
    .filter((x): x is string => typeof x === 'string' && x.trim() !== '')
    .map((x) => (/[.!?]$/.test(x.trim()) ? x.trim() : `${x.trim()}.`))
    .join(' ');
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * A range of local dates in words, month first as Canadian business text reads: "Oct 1–15, 2026",
 * "Sep 28 – Oct 3, 2026", "Dec 28, 2026 – Jan 3, 2027", or "Oct 15, 2026" for one day. For text the
 * product writes (agents' answers, messages), never ISO dates.
 */
export function dateRangeText(from: LocalDate, to: LocalDate): string {
  if (!isLocalDate(from) || !isLocalDate(to)) throw new RangeError(`invalid local dates: ${from}, ${to}`);
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  const mon = (m: number) => MONTHS[m - 1]!;
  if (from === to) return `${mon(fm)} ${fd}, ${fy}`;
  if (fy === ty && fm === tm) return `${mon(fm)} ${fd}–${td}, ${ty}`;
  if (fy === ty) return `${mon(fm)} ${fd} – ${mon(tm)} ${td}, ${ty}`;
  return `${mon(fm)} ${fd}, ${fy} – ${mon(tm)} ${td}, ${ty}`;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// ── clock ──

export interface Clock {
  now(): Date;
}

export const systemClock: Clock = { now: () => new Date() };

// The real wall clock, whatever Date tells in this process (QA-679). In the sandbox, Date tells the
// sandbox's moved time (tenancy's sandbox-time.ts), which product logic wants. A signature that a
// service outside the process checks against its own clock, such as the file store's SigV4 dates,
// must carry real time instead, or it is refused as skewed. Date as this module first saw it, and
// the real Date the sandbox hands over when it moves Date.
let RealDate: DateConstructor = globalThis.Date;

/** Now on the real wall clock: never moved by the sandbox. */
export const realNow = (): Date => new RealDate();

/** The sandbox's install of its time, before it replaces Date: the real one, for realNow. */
export function keepRealDate(real: DateConstructor): void {
  RealDate = real;
}

export interface FixedClock extends Clock {
  set(instant: Instant | Date): void;
  advance(ms: number): void;
}

/** A controllable clock for tests and the sync simulator. */
export function fixedClock(start: Instant | Date): FixedClock {
  let t = new Date(start).getTime();
  return {
    now: () => new Date(t),
    set: (i) => {
      t = new Date(i).getTime();
    },
    advance: (ms) => {
      t += ms;
    },
  };
}
